/**
 * 3D Tiles loader — 1:1 port of ui/src/viewer/tiles-loader.js from the
 * desktop viewer.
 *
 * Two coordinate modes:
 *
 *   - 'ecef'      : tile vertices are ECEF Cartesian (DJI Terra default).
 *                   We build a local ENU tangent plane at the bbox centre
 *                   and apply ECEF→ENU + γ correction so the rest of the
 *                   pipeline sees Z-up UTM-local coords.
 *
 *   - 'utm-local' : our pipeline.tiles output — vertices are already in
 *                   scene space (the 3d-tiles-renderer's internal
 *                   _upRotationMatrix has done the Y-up→Z-up swap).
 *                   No additional transform; just frame the camera.
 *
 * Caller picks the mode via `detectMode(tilesetUrl)`.
 */

import {
  Box3,
  Camera,
  DoubleSide,
  Group,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  SRGBColorSpace,
  Vector3,
  WebGLRenderer,
} from 'three'
import { TilesRenderer } from '3d-tiles-renderer'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import proj4 from 'proj4'
import { CoordinateManager } from './Coordinates'
import type { Controls } from './Controls'

export type TilesetMode = 'ecef' | 'utm-local'

export interface LoadedTiles {
  tilesRenderer: TilesRenderer
  group: Group
  /**
   * Every tile content scene currently held in memory (loaded, not yet
   * disposed) — whether or not the renderer is displaying it right now. The
   * renderer *removes* a tile's scene from `group` when it culls it (e.g. the
   * single tile gets frustum-culled the moment you zoom in close), so a raycast
   * against `group` alone finds nothing. Picking falls back to these so you can
   * still measure geometry that exists but is momentarily hidden.
   */
  loadedScenes: Set<Object3D>
}

// ─── ECEF / WGS84 / UTM helpers (1:1 from tiles-loader.js) ──────────────────

const _WGS84_A = 6378137.0
const _WGS84_E2 = 0.00669437999014

function ecefToWgs84(x: number, y: number, z: number): { lat: number; lon: number; alt: number } {
  const lon = Math.atan2(y, x)
  const p = Math.sqrt(x * x + y * y)
  let lat = Math.atan2(z, p * (1 - _WGS84_E2))
  for (let i = 0; i < 5; i++) {
    const sinLat = Math.sin(lat)
    const N = _WGS84_A / Math.sqrt(1 - _WGS84_E2 * sinLat * sinLat)
    lat = Math.atan2(z + _WGS84_E2 * N * sinLat, p)
  }
  const sinLat = Math.sin(lat)
  const N = _WGS84_A / Math.sqrt(1 - _WGS84_E2 * sinLat * sinLat)
  const alt = p / Math.cos(lat) - N
  return { lat, lon, alt }
}

interface UtmResult {
  utmE: number
  utmN: number
  crsCode: string
  gammaRad: number
  zone: number
}

function wgs84ToUtm(latRad: number, lonRad: number): UtmResult {
  const lonDeg = (lonRad * 180) / Math.PI
  const latDeg = (latRad * 180) / Math.PI
  const zone = Math.floor((lonDeg + 180) / 6) + 1
  const epsg = latDeg >= 0 ? `EPSG:258${zone}` : `EPSG:327${zone}`
  const crsCode = epsg === 'EPSG:25832' || epsg === 'EPSG:25833' ? epsg : 'EPSG:25833'
  const [utmE, utmN] = proj4('EPSG:4326', crsCode, [lonDeg, latDeg])
  const centralMeridianDeg = zone * 6 - 183
  const gammaRad = ((lonDeg - centralMeridianDeg) * Math.PI) / 180 * Math.sin(latRad)
  return { utmE, utmN, crsCode, gammaRad, zone }
}

// ─── Mode detection (web replacement for findTileset listDir trick) ─────────

/**
 * Probe for a `tileset_settings.json` sibling.  If it exists and says
 * `coordinate_system: "utm-local-baked"`, we're loading our pipeline's
 * output (UTM-local mode).  Otherwise we treat it as ECEF (DJI Terra
 * and other Cesium-shaped tilesets).
 */
export async function detectMode(tilesetUrl: string): Promise<TilesetMode> {
  const settingsUrl = tilesetUrl.replace(/tileset\.json(\?.*)?$/, 'tileset_settings.json$1')
  if (settingsUrl === tilesetUrl) return 'ecef'
  try {
    const resp = await fetch(settingsUrl, { credentials: 'omit' })
    if (!resp.ok) return 'ecef'
    const ct = resp.headers.get('content-type') ?? ''
    if (!ct.includes('json')) return 'ecef'
    const settings = (await resp.json()) as { coordinate_system?: string }
    return settings.coordinate_system === 'utm-local-baked' ? 'utm-local' : 'ecef'
  } catch {
    return 'ecef'
  }
}

// ─── loadTiles — literal port of 3DRoad tiles-loader.js:112-315 ─────────────

export function loadTiles(
  scene: Object3D,
  camera: Camera,
  renderer: WebGLRenderer,
  tilesetUrl: string,
  controls: Controls | null = null,
  coords: CoordinateManager | null = null,
  onReady: (() => void) | null = null,
  mode: TilesetMode = 'ecef',
): LoadedTiles {
  const tilesRenderer = new TilesRenderer(tilesetUrl)

  // LOD quality
  tilesRenderer.errorTarget = 1.0
  tilesRenderer.maxDepth = 100
  tilesRenderer.loadSiblings = true
  tilesRenderer.displayActiveTiles = true

  // Parallel loading + cache
  try {
    const tr = tilesRenderer as unknown as {
      downloadQueue?: { maxJobs: number }
      parseQueue?: { maxJobs: number }
      lruCache?: { minSize: number; maxSize: number }
    }
    if (tr.downloadQueue) tr.downloadQueue.maxJobs = 10
    if (tr.parseQueue) tr.parseQueue.maxJobs = 4
    if (tr.lruCache) {
      tr.lruCache.minSize = 600
      tr.lruCache.maxSize = 1500
    }
  } catch {
    /* older API */
  }

  // Tile content scenes held in memory — the picking fallback set (see
  // LoadedTiles.loadedScenes). Populated on load-model, pruned on dispose-model.
  const loadedScenes = new Set<Object3D>()
  {
    const tr = tilesRenderer as unknown as {
      addEventListener: (type: string, cb: (e: { scene?: Object3D }) => void) => void
    }
    tr.addEventListener('dispose-model', (e) => {
      if (e.scene) loadedScenes.delete(e.scene)
    })
  }

  // DRACOLoader + GLTFLoader for compressed b3dm contents
  const dracoLoader = new DRACOLoader()
  dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/')
  dracoLoader.setDecoderConfig({ type: 'js' })
  const gltfLoader = new GLTFLoader()
  gltfLoader.setDRACOLoader(dracoLoader)
  const trAny = tilesRenderer as unknown as {
    manager: { addHandler: (regex: RegExp, loader: GLTFLoader) => void }
  }
  trAny.manager.addHandler(/\.gltf$|\.glb$/i, gltfLoader)

  tilesRenderer.setCamera(camera)
  tilesRenderer.setResolutionFromRenderer(camera, renderer)

  const tilesGroup = new Group()

  let _cameraInitialized = false
  tilesRenderer.addEventListener('load-tileset', () => {
    if (_cameraInitialized) return
    const box = new Box3()
    const tr = tilesRenderer as unknown as { getBoundingBox?: (b: Box3) => void }
    if (tr.getBoundingBox) tr.getBoundingBox(box)
    if (box.isEmpty()) return

    const center = new Vector3()
    box.getCenter(center)
    const size = new Vector3()
    box.getSize(size)
    const maxDim = Math.max(size.x, size.y, size.z)

    // ─── Self-correcting mode override ───
    // Real UTM-local content lives in scene-space (tens of km from origin
    // at the absolute worst); real ECEF content is ~6.4 × 10⁶ m from the
    // Earth's centre.  If the tileset_settings.json hint disagrees with
    // what the renderer reports, the actual coords are source of truth.
    const distFromOrigin = Math.sqrt(
      center.x * center.x + center.y * center.y + center.z * center.z,
    )
    if (mode === 'utm-local' && distFromOrigin > 1_000_000) {
      console.warn(
        `[Tiles] tileset_settings.json claims utm-local but bbox centre |c|=${distFromOrigin.toFixed(0)} m (ECEF range). Overriding to ECEF.`,
      )
      mode = 'ecef'
    } else if (mode === 'ecef' && distFromOrigin < 1_000_000) {
      console.warn(
        `[Tiles] no settings sibling but bbox centre |c|=${distFromOrigin.toFixed(0)} m (scene-local range). Overriding to utm-local.`,
      )
      mode = 'utm-local'
    }

    // ─── UTM-local mode (pipeline.tiles output) ───
    if (mode === 'utm-local') {
      // ── Never frustum-cull these single-area models ──
      // 3d-tiles-renderer culls a tile the instant its bounding volume leaves the
      // view frustum, and displayActiveTiles does NOT rescue a self-out-of-frustum
      // root (the traversal early-returns on !inFrustum before marking it active),
      // so the model vanished at certain orbit angles / when zooming in close even
      // though its geometry is on screen. The bounding box is correct — this is
      // purely the renderer culling the lone tile. Force inView = true (after the
      // real error/distance are computed, so LOD still works). Installed here, once
      // mode is finalised (post self-correction), so ECEF tilesets keep culling.
      const tr2 = tilesRenderer as unknown as {
        calculateTileViewErrorWithPlugin?: (tile: unknown, target: { inView: boolean }) => void
      }
      const origCalcViewError = tr2.calculateTileViewErrorWithPlugin?.bind(tilesRenderer)
      if (origCalcViewError) {
        tr2.calculateTileViewErrorWithPlugin = (tile, target) => {
          origCalcViewError(tile, target)
          target.inView = true
        }
      }

      console.log(
        `[Tiles] UTM-local mode — bounding-box center (scene): (${center.x.toFixed(2)}, ${center.y.toFixed(2)}, ${center.z.toFixed(2)}) size: ${maxDim.toFixed(2)}`,
      )
      camera.position.set(
        center.x + maxDim * 1.2,
        center.y - maxDim * 1.2,
        center.z + maxDim * 0.8,
      )
      camera.up.set(0, 0, 1)
      camera.lookAt(center)
      _applyNearFar(camera, maxDim)
      if (controls) {
        controls.orbit.target.copy(center)
        controls.orbit.minDistance = 0.5
        controls.orbit.maxDistance = maxDim * 50
        controls.update()
      }
      _cameraInitialized = true
      onReady?.()
      return
    }

    // ─── ECEF mode (DJI Terra and similar) ───
    console.log(
      `[Tiles] ECEF center: (${center.x.toFixed(0)}, ${center.y.toFixed(0)}, ${center.z.toFixed(0)}) size: ${maxDim.toFixed(0)}`,
    )

    const cx = center.x
    const cy = center.y
    const cz = center.z
    const len = Math.sqrt(cx * cx + cy * cy + cz * cz)

    // ENU basis at centre
    const up = new Vector3(cx / len, cy / len, cz / len)
    const lonRad = Math.atan2(cy, cx)
    const east = new Vector3(-Math.sin(lonRad), Math.cos(lonRad), 0)
    const north = new Vector3().crossVectors(up, east).normalize()
    east.crossVectors(north, up).normalize()

    const rotMatrix = new Matrix4().makeBasis(east, north, up)
    const invRot = rotMatrix.clone().invert()

    // Compute UTM + γ
    let gammaRad = 0
    if (coords) {
      try {
        const { lat, lon, alt } = ecefToWgs84(cx, cy, cz)
        const { utmE, utmN, crsCode, gammaRad: gr, zone } = wgs84ToUtm(lat, lon)
        gammaRad = gr
        const firstSetup = !coords.loaded
        if (firstSetup) {
          coords.eastingOffset = utmE
          coords.northingOffset = utmN
          coords.altitudeOffset = alt
          coords.sourceCRS = crsCode
          coords.displayCRS = crsCode
          coords.loaded = true
        }
        coords.setEcefTransform({
          center: [cx, cy, cz],
          rot: [east.x, east.y, east.z, north.x, north.y, north.z, up.x, up.y, up.z],
          gammaRad,
        })
        console.log(
          `[Tiles] UTM offset: E=${utmE.toFixed(3)} N=${utmN.toFixed(3)} alt=${alt.toFixed(3)} zone=${zone} γ=${((gr * 180) / Math.PI).toFixed(4)}° — ECEF exact mode ON`,
        )
        if (firstSetup) onReady?.()
      } catch (e) {
        console.warn('[Tiles] Failed to compute UTM offset:', e)
      }
    }

    // Transform: translate ECEF center to origin, rotate to ENU,
    // rotate by -γ around Z so ENU aligns with UTM grid.
    tilesGroup.matrixAutoUpdate = false
    const m = new Matrix4().makeTranslation(-cx, -cy, -cz)
    m.premultiply(invRot)
    if (gammaRad !== 0) {
      const convRot = new Matrix4().makeRotationZ(-gammaRad)
      m.premultiply(convRot)
    }
    tilesGroup.matrix.copy(m)
    tilesGroup.matrixWorldNeedsUpdate = true

    // Camera in ENU frame (model centered at origin, Z=up)
    camera.position.set(0, -maxDim * 1.2, maxDim * 0.8)
    camera.up.set(0, 0, 1)
    camera.lookAt(0, 0, 0)
    _applyNearFar(camera, maxDim)
    if (controls) {
      controls.orbit.target.set(0, 0, 0)
      controls.orbit.minDistance = 0.5
      controls.orbit.maxDistance = maxDim * 50
      controls.update()
    }
    _cameraInitialized = true
  })

  // Material conversion: GLTFLoader hands us MeshStandardMaterial (PBR).
  // Scene has no lights → would render black.  Swap to MeshBasicMaterial
  // so the photogrammetric texture is visible without scene lights.
  tilesRenderer.addEventListener('load-model', (e: { scene?: Object3D }) => {
    const modelScene = e.scene
    if (!modelScene) return
    // Retain for the picking fallback (raycast survives renderer culling).
    loadedScenes.add(modelScene)
    modelScene.traverse((child: Object3D) => {
      const m = child as Mesh
      if (!m.isMesh || !m.material) return
      const oldMat = m.material as MeshStandardMaterial
      const texMap = oldMat.map ?? null
      if (texMap) texMap.colorSpace = SRGBColorSpace
      m.material = new MeshBasicMaterial({
        map: texMap,
        color: texMap ? 0xffffff : 0xcccccc,
        side: DoubleSide,
      })
      m.frustumCulled = false
      if (typeof oldMat.dispose === 'function') oldMat.dispose()
    })
  })

  tilesGroup.add(tilesRenderer.group)
  scene.add(tilesGroup)
  tilesRenderer.update()

  return { tilesRenderer, group: tilesGroup, loadedScenes }
}

function _applyNearFar(camera: Camera, maxDim: number): void {
  const persp = camera as { near?: number; far?: number; updateProjectionMatrix?: () => void }
  // Smaller near so close-up surfaces (e.g. measuring the bottom of a pit a
  // few cm from the camera) aren't clipped away. Scaled to the model so big
  // and tiny tilesets both behave; clamped to a sane window.
  persp.near = Math.min(0.1, Math.max(0.02, maxDim * 0.002))
  persp.far = maxDim * 100
  persp.updateProjectionMatrix?.()
}

/** Update — call each animate frame. */
export function updateTiles(tilesRenderer: TilesRenderer, camera: Camera, renderer: WebGLRenderer): void {
  camera.updateMatrixWorld()
  tilesRenderer.setResolutionFromRenderer(camera, renderer)
  tilesRenderer.update()
}
